// utils/conflictHighlighter.js - Visual conflict highlighting utilities
import { FIELD_COMPARISON } from './comparisonEngine';

/**
 * Conflict highlighter utility for visual styling of comparison differences
 * Provides consistent styling across comparison components
 */

/**
 * Get border color classes based on field comparison status
 */
export const getBorderColor = (status) => {
  switch (status) {
    case FIELD_COMPARISON.MATCH:
      return 'border-green-200 dark:border-green-600';
    case FIELD_COMPARISON.DIFFER:
      return 'border-red-200 dark:border-red-600';
    case FIELD_COMPARISON.MISSING:
      return 'border-yellow-200 dark:border-yellow-600';
    case FIELD_COMPARISON.INVALID:
      return 'border-red-300 dark:border-red-500';
    default:
      return 'border-gray-200 dark:border-gray-600';
  }
};

/**
 * Get background color classes based on field comparison status
 */
export const getBackgroundColor = (status, opacity = 'normal') => {
  const opacityClasses = {
    light: opacity === 'light' ? '/10' : '/20',
    normal: '/20',
    strong: '/30'
  };

  const suffix = opacityClasses[opacity] || opacityClasses.normal;

  switch (status) {
    case FIELD_COMPARISON.MATCH:
      return `bg-green-50 dark:bg-green-900${suffix}`;
    case FIELD_COMPARISON.DIFFER:
      return `bg-red-50 dark:bg-red-900${suffix}`;
    case FIELD_COMPARISON.MISSING:
      return `bg-yellow-50 dark:bg-yellow-900${suffix}`;
    case FIELD_COMPARISON.INVALID:
      return `bg-red-100 dark:bg-red-900/40`;
    default:
      return `bg-gray-50 dark:bg-gray-900${suffix}`;
  }
};

/**
 * Get text color classes based on field comparison status
 */
export const getTextColor = (status) => {
  switch (status) {
    case FIELD_COMPARISON.MATCH:
      return 'text-green-700 dark:text-green-300';
    case FIELD_COMPARISON.DIFFER:
      return 'text-red-700 dark:text-red-300';
    case FIELD_COMPARISON.MISSING:
      return 'text-yellow-700 dark:text-yellow-300';
    case FIELD_COMPARISON.INVALID:
      return 'text-red-800 dark:text-red-200';
    default:
      return 'text-gray-700 dark:text-gray-300';
  }
};

/**
 * Get hover color classes based on field comparison status
 */
export const getHoverColor = (status) => {
  switch (status) {
    case FIELD_COMPARISON.MATCH:
      return 'hover:bg-green-100 dark:hover:bg-green-900/30';
    case FIELD_COMPARISON.DIFFER:
      return 'hover:bg-red-100 dark:hover:bg-red-900/30';
    case FIELD_COMPARISON.MISSING:
      return 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30';
    case FIELD_COMPARISON.INVALID:
      return 'hover:bg-red-200 dark:hover:bg-red-900/40';
    default:
      return 'hover:bg-gray-100 dark:hover:bg-gray-800';
  }
};

/**
 * Get combined styling classes for a comparison status
 */
export const getComparisonStyling = (status, options = {}) => {
  const {
    includeBorder = true,
    includeBackground = true,
    includeText = false,
    includeHover = false,
    opacity = 'normal'
  } = options;

  const classes = [];

  if (includeBorder) {
    classes.push(getBorderColor(status));
  }

  if (includeBackground) {
    classes.push(getBackgroundColor(status, opacity));
  }

  if (includeText) {
    classes.push(getTextColor(status));
  }

  if (includeHover) {
    classes.push(getHoverColor(status));
  }

  return classes.join(' ');
};

/**
 * Get button styling based on action type and state
 */
export const getActionButtonStyling = (actionType, options = {}) => {
  const { 
    disabled = false, 
    variant = 'primary',
    size = 'normal'
  } = options;

  const baseClasses = [
    'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2'
  ];

  // Size classes
  const sizeClasses = {
    small: 'px-3 py-1.5 text-sm',
    normal: 'px-4 py-2 text-sm',
    large: 'px-6 py-3 text-base'
  };

  baseClasses.push(sizeClasses[size] || sizeClasses.normal);

  // Disabled state
  if (disabled) {
    baseClasses.push('opacity-50 cursor-not-allowed');
    return baseClasses.join(' ');
  }

  // Action-specific styling
  switch (actionType) {
    case 'approve':
      if (variant === 'primary') {
        baseClasses.push(
          'bg-green-600 hover:bg-green-700 text-white',
          'focus:ring-green-500 dark:focus:ring-green-400'
        );
      } else {
        baseClasses.push(
          'bg-green-50 hover:bg-green-100 text-green-700 border border-green-200',
          'dark:bg-green-900/20 dark:hover:bg-green-900/30 dark:text-green-300 dark:border-green-600'
        );
      }
      break;

    case 'reject':
      if (variant === 'primary') {
        baseClasses.push(
          'bg-red-600 hover:bg-red-700 text-white',
          'focus:ring-red-500 dark:focus:ring-red-400'
        );
      } else {
        baseClasses.push(
          'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200',
          'dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:text-red-300 dark:border-red-600'
        );
      }
      break;

    case 'modify':
      if (variant === 'primary') {
        baseClasses.push(
          'bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900',
          'focus:ring-[#31C590]'
        );
      } else {
        baseClasses.push(
          'bg-[#22262f] hover:bg-[#2a2f39] text-[#23F8B5] border border-[#31C590]/40',
          'focus:ring-[#31C590]'
        );
      }
      break;

    case 'neutral':
    default:
      if (variant === 'primary') {
        baseClasses.push(
          'bg-gray-600 hover:bg-gray-700 text-white',
          'focus:ring-gray-500 dark:focus:ring-gray-400'
        );
      } else {
        baseClasses.push(
          'bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200',
          'dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
        );
      }
      break;
  }

  return baseClasses.join(' ');
};